// The level builder's "open a map" window (only in the builder, so the main menu stays simple): one of
// our maps, or a published player map, copied into Your levels to build on. Our maps show a picture
// of their course; player maps are searched like the Player maps tab.

import type { LevelBox } from '../maps/boxlevel/types';
import type { PlayerMap } from '../online/client';

export interface MapOpenHost {
  /** Our maps, in order. */
  builtins(): { name: string; title: string; difficulty: number; stages: number }[];
  /** The pieces of one of our maps (for its picture). */
  builtinBoxes(name: string): LevelBox[];
  /** Player maps, best rated first, matching `query`. */
  search(query: string, page: number): Promise<{ maps: PlayerMap[]; more: boolean }>;
  /** Copy a map into Your levels and build on it. */
  openBuiltin(name: string): void;
  openPlayer(map: PlayerMap): Promise<void>;
  /** Opened (true) or closed (false): free the mouse, or take it back (inside a click). */
  shown(open: boolean): void;
}

const $ = (id: string) => document.getElementById(id)!;
const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls = '', text = ''): HTMLElementTagNameMap[K] => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text) e.textContent = text;
  return e;
};

const STARS = ['', '★', '★★', '★★★', '★★★★', '★★★★★'];
const TIER = ['', 'Easy', 'Medium', 'Hard', 'Expert', 'Insane'];

/** Colour of a piece in a course sketch. */
const SKETCH: Partial<Record<LevelBox['kind'], string>> = {
  start: '#4fd27a',
  checkpoint: '#5ad1ff',
  end: '#ff5a5a',
  boost: '#ffd23a',
};

/** A course seen from above: its pieces (not scenery) fitted into the picture, start to end. */
function sketch(boxes: LevelBox[]): string {
  const W = 280;
  const H = 200;
  const pieces = boxes.filter((b) => b.kind !== 'deco');
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const b of pieces) {
    x0 = Math.min(x0, b.min[0]);
    y0 = Math.min(y0, b.min[1]);
    x1 = Math.max(x1, b.max[0]);
    y1 = Math.max(y1, b.max[1]);
  }
  const c = document.createElement('canvas');
  c.width = W;
  c.height = H;
  const g = c.getContext('2d')!;
  if (!pieces.length) return c.toDataURL();
  const k = Math.min((W - 16) / Math.max(1, y1 - y0), (H - 16) / Math.max(1, x1 - x0));
  // Source X (forward) up the picture, Y (left) to the left.
  const px = (y: number) => W / 2 + ((y0 + y1) / 2 - y) * k;
  const py = (x: number) => H / 2 - (x - (x0 + x1) / 2) * k;
  // Platforms last, so they sit on top of the blocks around them.
  const order = [...pieces].sort((a, b) => (SKETCH[a.kind] ? 1 : 0) - (SKETCH[b.kind] ? 1 : 0));
  for (const b of order) {
    g.fillStyle = SKETCH[b.kind] ?? b.color;
    const w = Math.max(2, (b.max[1] - b.min[1]) * k);
    const h = Math.max(2, (b.max[0] - b.min[0]) * k);
    g.fillRect(px(b.max[1]), py(b.max[0]), w, h);
  }
  return c.toDataURL();
}

export class MapOpen {
  private filled = false;
  private query = '';
  private page = 0;
  private searchTimer = 0;
  private loading = false;

  constructor(private readonly host: MapOpenHost) {
    $('mapOpenClose').addEventListener('click', () => this.close(true));
    for (const b of document.querySelectorAll<HTMLElement>('#mapOpenTabs button'))
      b.addEventListener('click', () => this.showTab(b.dataset.tab!));
    $('mapOpenSearch').addEventListener('input', () => {
      clearTimeout(this.searchTimer);
      this.searchTimer = window.setTimeout(() => void this.load(true), 350);
    });
    $('mapOpenMore').addEventListener('click', () => void this.load(false));
  }

  get isOpen() {
    return !$('mapOpen').hidden;
  }

  open() {
    if (!this.filled) this.fillBuiltins();
    $('mapOpen').hidden = false;
    this.host.shown(true);
  }

  /** Close; with `relock`, take the mouse back (must run inside a click). */
  close(relock: boolean) {
    if (!this.isOpen) return;
    $('mapOpen').hidden = true;
    if (relock) this.host.shown(false);
  }

  private showTab(id: string) {
    for (const b of document.querySelectorAll<HTMLElement>('#mapOpenTabs button'))
      b.classList.toggle('on', b.dataset.tab === id);
    for (const p of document.querySelectorAll<HTMLElement>('.mapOpenPage')) p.hidden = p.dataset.tab !== id;
    if (id === 'players' && !$('mapOpenList').childElementCount) void this.load(true);
  }

  private fillBuiltins() {
    this.filled = true;
    const page = document.querySelector<HTMLElement>('.mapOpenPage[data-tab="builtin"]')!;
    let tier = 0;
    let grid: HTMLElement | null = null;
    for (const m of this.host.builtins()) {
      if (m.difficulty !== tier || !grid) {
        tier = m.difficulty;
        page.append(el('h4', 'catalogGroup', `${STARS[tier] ?? ''} ${TIER[tier] ?? ''}`));
        grid = el('div', 'mapOpenGrid');
        page.append(grid);
      }
      const card = this.card(m.title, `${m.stages} stages`, () => {
        this.close(false);
        this.host.openBuiltin(m.name);
      });
      const img = card.querySelector('img')!;
      // A sketch of the course as it scrolls into view (generating a map takes a moment).
      new IntersectionObserver((entries, obs) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        obs.disconnect();
        img.src = sketch(this.host.builtinBoxes(m.name));
      }).observe(card);
      grid.append(card);
    }
  }

  private card(title: string, sub: string, pick: () => void): HTMLButtonElement {
    const b = el('button', 'tile mapOpenCard');
    b.type = 'button';
    const img = el('img');
    img.alt = '';
    b.append(img, el('b', '', title), el('small', '', sub));
    b.addEventListener('click', pick);
    return b;
  }

  private async load(fresh: boolean) {
    if (this.loading) return;
    this.loading = true;
    const list = $('mapOpenList');
    if (fresh) {
      this.query = ($('mapOpenSearch') as HTMLInputElement).value.trim();
      this.page = 0;
      list.replaceChildren();
    }
    $('mapOpenMsg').textContent = 'Loading…';
    try {
      const r = await this.host.search(this.query, this.page);
      this.page++;
      for (const m of r.maps) {
        const rating = m.rating === null ? '' : ` · ★ ${m.rating.toFixed(1)}`;
        const card = this.card(m.title, `by ${m.author} · ${m.pieces} pieces${rating}`, () => {
          $('mapOpenMsg').textContent = `Opening ${m.title}…`;
          void this.host
            .openPlayer(m)
            .then(() => this.close(false))
            .catch((e: Error) => ($('mapOpenMsg').textContent = e.message));
        });
        card.querySelector('img')!.remove();
        list.append(card);
      }
      $('mapOpenMore').hidden = !r.more;
      $('mapOpenMsg').textContent = list.childElementCount
        ? ''
        : this.query
          ? `No player maps match “${this.query}”.`
          : 'No player maps yet.';
    } catch {
      $('mapOpenMsg').textContent = "Couldn't reach the server: player maps need a connection.";
    } finally {
      this.loading = false;
    }
  }
}
